import { Injectable } from '@angular/core'

@Injectable({ providedIn: 'root' })
export class AgentsSearchService {
  replaceTurkishChars(input: string): string {
    const map: Record<string, string> = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G',
      'ı': 'i', 'I': 'I',
      'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U',
    }

    return (input || '')
      .split('')
      .map(char => map[char] ?? char)
      .join('')
  }

  normalizeText(value: any): string {
    const s = (value ?? '').toString()
    const lower = s.toLowerCase().replace(/\u00A0/g, ' ')
    const replaced = this.replaceTurkishChars(lower)
    const deAccented = replaced.normalize('NFD').replace(/\p{Diacritic}+/gu, '')
    return deAccented.replace(/\s+/g, ' ').trim()
  }

  /**
   * Checks whether a phone number candidate matches a search query using a robust strategy.
   *
   * Behavior parity with ArchivedAgentsComponent.hasPhoneNumber:
   * - Treats inputs as strings and returns false if either is empty
   * - Requires a minimum of 3 digits in the search query to avoid noisy matches
   * - Generates multiple variants of the candidate number:
   *   - original
   *   - without " 0" (removes occasional trunk prefix after country code)
   *   - without "+"
   *   - without country code when the number contains spaces (drops the first token)
   *   - without leading "0 " after dropping country code
   * - Compares digit-only representations and accepts both directions of substring match
   *   (variant includes searchDigits OR searchDigits includes variant) to handle very short
   *   local numbers typed by users and partially formatted database values
   *
   * @param query free-form user input (may contain spaces, plus, parentheses)
   * @param candidate stored phone number string to be matched against
   * @returns true if any normalized variant matches, otherwise false
   */
  hasPhoneNumber(query: string, candidate: any): boolean {
    const isString = (v: any) => typeof v === 'string' && v.length > 0
    const q = isString(query) ? query as string : ''
    const c = isString(candidate) ? (candidate as string) : ''
    if (!q || !c) return false

    const onlyDigits = (v: string) => v.replace(/\D/g, '')
    const searchDigits = onlyDigits(q)
    if (searchDigits.length < 3) return false

    const variants: string[] = []
    variants.push(c)
    variants.push(c.replaceAll(' 0', ''))
    variants.push(c.replaceAll('+', ''))

    const parts = c.split(' ')
    if (parts.length > 1) {
      const withoutCc = parts.slice(1).join(' ')
      variants.push(withoutCc)
      variants.push(withoutCc.replaceAll('0 ', ''))
    }

    const digitVariants = Array.from(new Set(
      variants
        .map(v => onlyDigits(v))
        .filter(v => v.length >= 3)
    ))

    return digitVariants.some(v => v.includes(searchDigits) || searchDigits.includes(v))
  }

  /**
   * Filters agents by searching only in first and last names (for project view)
   * @param agentArray Array of agents to filter
   * @param searchTerm Search term to match against names
   * @returns Filtered array of agents
   */
  filterAgentsByNameOnly(agentArray: any[], searchTerm: string): any[] {
    const normalizedSearchValue = this.normalizeText(searchTerm)
    if (!normalizedSearchValue) return [...agentArray]

    return agentArray.filter((agent: any) => {
      const first = this.normalizeText(agent?.firstname ?? '')
      const last = this.normalizeText(agent?.lastname ?? '')
      const fallbackName = this.normalizeText(agent?.name ?? '')
      const normalizedName = `${first} ${last}`.trim() || fallbackName

      return normalizedName && (
        normalizedName.includes(normalizedSearchValue) ||
        normalizedName.split(' ').some((p: string) => p.startsWith(normalizedSearchValue))
      )
    })
  }

  filterAgents(agentArray: any[], searchTerm: string): any[] {
    const normalizedSearchValue = this.normalizeText(searchTerm)
    if (!normalizedSearchValue) return [...agentArray]

    const excludedFields = [
      'profileImageSrc',
      'voiceFiles',
      'videoFiles',
      'agentProfilePhoto',
      'internalNote',
      'project',
      'updatedAt',
      'partnerId',
    ]

    return agentArray.filter((agent: any) => {
      const first = this.normalizeText(agent?.firstname ?? '')
      const last = this.normalizeText(agent?.lastname ?? '')
      const fallbackName = this.normalizeText(agent?.name ?? '')
      const normalizedName = `${first} ${last}`.trim() || fallbackName

      if (normalizedName && (
        normalizedName.includes(normalizedSearchValue) ||
        normalizedName.split(' ').some((p: string) => p.startsWith(normalizedSearchValue))
      )) return true

      const matched = Object.keys(agent || {}).some(key => {
        if (excludedFields.includes(key)) return false
        const value = (agent as any)[key]
        if (key === 'phoneNumber') return this.hasPhoneNumber(normalizedSearchValue, value)
        if (key === 'applicationReceivedAt' && value) {
          const dateStr = new Date(value).toLocaleDateString('de-DE')
          const nv = this.normalizeText(dateStr)
          if (nv.includes(normalizedSearchValue)) return true
        }
        if (typeof value === 'string') {
          const nv = this.normalizeText(value)
          if (nv.includes(normalizedSearchValue)) return true
        }
        return false
      })

      if (matched) return true
      return false
    })
  }
}
